import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    InputAdornment,
    Popover,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { CloseRounded, EmojiEmotionsRounded, SearchRounded } from '@mui/icons-material';
import {
    LUCIDE_ICON_CATALOG,
    LUCIDE_ICON_GROUPS,
    filterLucideIcons,
    findLucideMenuIcon,
} from '@/lib/lucideMenuIcons';

/**
 * Seletor Lucide para o admin Eurobem (MUI).
 * Popover — funciona dentro de Dialog MUI sem ficar atrás.
 */
export default function SelectIconAdmin({ value, name, onChange }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [search, setSearch] = useState('');
    const [groupId, setGroupId] = useState('all');

    const open = Boolean(anchorEl);
    const selected = useMemo(() => findLucideMenuIcon(value), [value]);
    const SelectedIcon = selected?.Component;

    const filtered = useMemo(
        () => filterLucideIcons(LUCIDE_ICON_CATALOG, search, groupId),
        [search, groupId],
    );

    const handleOpen = event => setAnchorEl(event.currentTarget);

    const handleClose = () => {
        setAnchorEl(null);
        setSearch('');
        setGroupId('all');
    };

    const handleSelect = item => {
        onChange?.(name, item.value);
        handleClose();
    };

    const handleClear = event => {
        event?.stopPropagation?.();
        onChange?.(name, '');
        handleClose();
    };

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Tooltip title={selected ? selected.label : 'Selecionar ícone'}>
                    <Button
                        variant="outlined"
                        onClick={handleOpen}
                        sx={{ height: 40, minWidth: 40, px: 1 }}
                    >
                        {SelectedIcon ? (
                            <SelectedIcon size={20} />
                        ) : (
                            <EmojiEmotionsRounded fontSize="small" color="action" />
                        )}
                    </Button>
                </Tooltip>
                {selected ? (
                    <Tooltip title="Remover ícone">
                        <IconButton size="small" onClick={handleClear} aria-label="Remover ícone">
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Stack>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableRestoreFocus
                disableScrollLock
                slotProps={{
                    paper: {
                        sx: {
                            width: { xs: 'min(100vw - 24px)', sm: 400 },
                            p: 1.5,
                            mt: 1,
                        },
                    },
                }}
                sx={{ zIndex: theme => theme.zIndex.modal + 10 }}
            >
                <Stack spacing={1.5}>
                    <Typography variant="subtitle2">Selecionar ícone</Typography>

                    <TextField
                        size="small"
                        placeholder="Buscar…"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        fullWidth
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRounded fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        <Chip
                            size="small"
                            label="Todos"
                            color={groupId === 'all' ? 'primary' : 'default'}
                            variant={groupId === 'all' ? 'filled' : 'outlined'}
                            onClick={() => setGroupId('all')}
                        />
                        {LUCIDE_ICON_GROUPS.map(group => (
                            <Chip
                                key={group.id}
                                size="small"
                                label={group.label}
                                color={groupId === group.id ? 'primary' : 'default'}
                                variant={groupId === group.id ? 'filled' : 'outlined'}
                                onClick={() => setGroupId(group.id)}
                            />
                        ))}
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 0.75,
                            maxHeight: 280,
                            overflowY: 'auto',
                        }}
                    >
                        {filtered.map(item => {
                            const Icon = item.Component;
                            const isActive = selected?.value === item.value;
                            return (
                                <Tooltip key={item.value} title={item.label} enterDelay={400}>
                                    <IconButton
                                        onClick={() => handleSelect(item)}
                                        sx={{
                                            border: 1,
                                            borderColor: isActive ? 'primary.main' : 'divider',
                                            borderRadius: 2,
                                            bgcolor: isActive ? 'action.selected' : 'transparent',
                                            aspectRatio: '1',
                                        }}
                                    >
                                        <Icon size={20} />
                                    </IconButton>
                                </Tooltip>
                            );
                        })}
                    </Box>

                    {filtered.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                            Nenhum ícone encontrado.
                        </Typography>
                    ) : null}

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button size="small" onClick={handleClear} disabled={!selected}>
                            Sem ícone
                        </Button>
                        {selected ? (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {selected.slug}
                            </Typography>
                        ) : null}
                    </Stack>
                </Stack>
            </Popover>
        </>
    );
}
